import React, { useRef, useEffect, useReducer, useState, useCallback } from 'react';
import { Notice } from '@amtrav/view-components';
import { useSpring } from 'react-spring';

// The useAmtravApi hook is for interacting with an API while tracking request in flight
// and errors.
// Basic usage:

// const [mySubmit, submitting, error] = useAmtravApi(async (values) => {
//   const payload = { ...values };
//   await actions.patchProfile(profileData.profileId, payload);
//   await actions.refreshProfile();
//   onClose();
// });
//
// ...
// ...
// ...
//
// const formik = useFormik({
//   onSubmit: mySubmit,
// });

// Note that the handler given does NOT use a try/catch, since we want the errors to be caught
// by the hook. You can supply any function that might throw and Error object.
// 'submitting' here will be true from when 'mySubmit' is called until the promise it returns
// is finished. 'error' will be null, unless there is an error, and it will be reset to null after
// 'mySubmit' is called again. Rerenders will be triggered at each state change.

const reducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return {
        working: false,
        err: null
      };
    case 'start':
      return {
        working: true,
        err: null
      };
    case 'setErr':
      return {
        working: false,
        err: action.payload
      };
    default:
      return state;
  }
};
function useAmtravApi(handler) {
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [state, dispatch] = useReducer(reducer, {
    working: false,
    err: null
  });
  const wrapped = async function () {
    let val;
    try {
      dispatch({
        type: 'start'
      });
      val = await handler(...arguments);
      if (mountedRef.current) {
        dispatch({
          type: 'reset'
        });
      }
    } catch (newErr) {
      dispatch({
        type: 'setErr',
        payload: newErr
      });
    }
    return val;
  };
  return [wrapped, state.working, state.err];
}
function useAmtravApiWithNotice(handler) {
  const [wrapped, working, err] = useAmtravApi(handler);
  return [wrapped, working, err ? /*#__PURE__*/React.createElement(Notice, {
    kind: "danger"
  }, err.message) : null];
}

// https://www.joshwcomeau.com/snippets/react-hooks/use-boop/
function useBoop(_ref) {
  let {
    x = 0,
    y = 0,
    rotation = 0,
    scale = 1,
    timing = 150,
    springConfig = {
      tension: 300,
      friction: 10
    }
  } = _ref;
  const [isBooped, setIsBooped] = React.useState(false);
  const style = useSpring({
    transform: isBooped ? `translate(${x}px, ${y}px)
         rotate(${rotation}deg)
         scale(${scale})` : `translate(0px, 0px)
         rotate(0deg)
         scale(1)`,
    config: springConfig
  });
  React.useEffect(() => {
    if (!isBooped) {
      return () => {};
    }
    const timeoutId = window.setTimeout(() => {
      setIsBooped(false);
    }, timing);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isBooped]);
  const trigger = React.useCallback(() => {
    setIsBooped(true);
  }, []);
  return [style, trigger];
}

function useDebounce(value, callback, delay) {
  const [newValue, setNewValue] = useState(value);
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(newValue);
      callback(newValue);
    }, delay);
    return () => clearTimeout(handler);
  }, [newValue]);

  // In most cases the debounced value isn't needed but we can return it anyway
  return [newValue, setNewValue, debouncedValue];
}

const makeInitialState = (pageSize, list, sorter, getId) => {
  const sorted = list.slice();
  sorted.sort(sorter);
  return {
    filterString: '',
    pageSize,
    pageIndex: 0,
    initialOrder: sorted.map(getId)
  };
};
const filterReducer = (state, _ref) => {
  let {
    type,
    payload
  } = _ref;
  switch (type) {
    case 'updateFilter':
      {
        return {
          ...state,
          filterString: payload,
          pageIndex: 0
        };
      }
    case 'clearFilter':
      {
        return {
          ...state,
          filterString: ''
        };
      }
    case 'nextPage':
      {
        return {
          ...state,
          pageIndex: state.pageIndex + 1
        };
      }
    case 'prevPage':
      {
        return {
          ...state,
          pageIndex: state.pageIndex - 1
        };
      }
    default:
      throw new Error('Action not supported in FilterList reducer');
  }
};
const matchSort = (list, idList, getId) => {
  return idList.map(id => list.find(item => getId(item) === id));
};
function usePaginator(_ref2, list) {
  let {
    initialPageSize,
    filter,
    sorter,
    useInitialOrder,
    getId
  } = _ref2;
  const [{
    pageIndex,
    pageSize,
    filterString,
    initialOrder
  }, dispatch] = useReducer(filterReducer, makeInitialState(initialPageSize, list, sorter, getId));
  const sorted = list.slice();
  if (sorter) {
    sorted.sort(sorter);
  }
  const visible = (useInitialOrder ? matchSort(list, initialOrder, getId) : sorted).filter(filter(filterString));
  const current = visible.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
  const state = {
    currentPage: current,
    pageIndex,
    pageSize,
    range: [pageIndex * pageSize + 1, Math.min((pageIndex + 1) * pageSize, visible.length)],
    hasNext: visible.length > (pageIndex + 1) * pageSize,
    hasPrev: pageIndex > 0,
    total: sorted.length,
    visibleCount: visible.length,
    filterString
  };
  const actions = {
    next: () => dispatch({
      type: 'nextPage'
    }),
    prev: () => dispatch({
      type: 'prevPage'
    }),
    updateFilter: payload => dispatch({
      type: 'updateFilter',
      payload
    }),
    clearFilter: () => dispatch({
      type: 'clearFilter'
    })
  };
  return [state, actions];
}

// https://www.joshwcomeau.com/snippets/react-hooks/use-boop/
function useWindowResize(callback) {
  useEffect(() => {
    let resizeId;
    window.addEventListener('resize', () => {
      // fires callback only when resizing is finished
      clearTimeout(resizeId);
      resizeId = setTimeout(callback, 100);
    });

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', callback);
  }, []);
}

function useTransitionHeight() {
  const [isVisible, setIsVisible] = useState(true);
  const [startingHeight, setStaringHeight] = useState(0);
  const [newHeight, setNewHeight] = useState(0);
  const ref = useRef();
  const springOpacity = useSpring({
    opacity: isVisible ? 1 : 0,
    config: {
      duration: 170
    },
    width: '100%'
  });
  const springHeight = useSpring({
    delay: newHeight ? 190 : 0,
    config: {
      duration: newHeight ? undefined : 0
    },
    from: {
      height: startingHeight
    },
    to: {
      height: newHeight || startingHeight
    }
  });
  const springStyles = {
    ...springOpacity,
    ...springHeight
  };
  const updateHeight = () => {
    if (ref.current) setNewHeight(ref.current.offsetHeight);
  };
  useWindowResize(updateHeight);
  useEffect(() => {
    if (ref.current) setStaringHeight(ref.current.offsetHeight);
  }, []);
  const trigger = useCallback(transitionCallback => {
    if (ref.current) {
      // fade out
      setIsVisible(false);

      // perform callback
      setTimeout(() => {
        transitionCallback();
      }, 170);

      // adjust height
      setTimeout(() => {
        setNewHeight(ref.current.offsetHeight);
      }, 180);

      // fade in
      setTimeout(() => {
        setIsVisible(true);
      }, 1100);
    }
  }, []);
  return [ref, springStyles, trigger];
}

export { useAmtravApi, useAmtravApiWithNotice, useBoop, useDebounce, usePaginator, useTransitionHeight, useWindowResize };
//# sourceMappingURL=index.esm.js.map
